// CadastreInfoComponent.js
import { useState, useEffect } from 'react';
import { EditableInputComponent, EditableSelectComponent, SectionHeader } from "components";
import { Appliances, Document, Eye, Plus } from "assets/icons"; // Replace with appropriate icons
import { toast } from "react-toastify";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { BuildingDTO } from 'shared/types/building';
import { UnitDTO } from 'shared/types/unit';
import { UnitConformityStatus } from 'shared/types/unit-conformity-info.dto';
import { ClimatisationDevice, ClimatisationInfo, ClimatisationSource, HeatingConnectedTo } from 'shared/types/climatisation';
import ButtonNew from 'components/buttonNew/buttonNew';
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from "@heroui/react";
import ClimatisationDeviceDetailModal from './climatisationDeviceDetailModal';
// Meters verhuisd naar voorzieningen (assets) — sectie verborgen.
// import GasMeters from './gasMeters';

interface ClimatisationInfoComponentProps {
    climatisationInfo: ClimatisationInfo;
    handleClimatisationInfoChangeInParent?: (value: any) => any;
    handleSaveInParent?: () => any;
    handleCloseParent?: () => any;
    building: BuildingDTO,
    unit: UnitDTO,
    parentEditMode?: boolean;
}

const ClimatisationInfoComponent = ({
    climatisationInfo,
    handleCloseParent,
    handleSaveInParent,
    building,
    unit,
    handleClimatisationInfoChangeInParent,
    parentEditMode = true,
}: ClimatisationInfoComponentProps) => {
    const [editMode, setEditMode] = useState(parentEditMode);
    const [editedClimatisationInfo, setEditedClimatisationInfo] = useState(climatisationInfo);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { session } = useSession();
    const [isAddDeviceModalOpen, setIsAddDeviceModalOpen] = useState(false);
    const [isDeviceDetailModalOpen, setIsDeviceDetailModalOpen] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<ClimatisationDevice | null>(null);
    const [climatisationDevices, setClimatisationDevices] = useState<ClimatisationDevice[]>([]);

    useEffect(() => {
        console.log(climatisationInfo);
        setEditedClimatisationInfo(climatisationInfo || { remarks: null, connectedTo: HeatingConnectedTo.UNKNOWN, climatisationDevices: [] });
        setClimatisationDevices(climatisationInfo?.climatisationDevices || []);
    }, []); // don't use the dependency here because it will refresh the component on tab switch

    const fetchDevices = async (climatisationInfoId: any) => {
        let res = await fetchWithAuth(
            `${API_URL}/climatisation/devices/${climatisationInfoId}`,
            {
                method: "GET",
            },
            session,
        );

        const climatisationDevices = await res.json();

        console.log(climatisationDevices);
        setEditedClimatisationInfo((prev: any) => ({ ...prev, climatisationDevices: climatisationDevices }));
        setClimatisationDevices(climatisationDevices);
    };


    const handleSave = async () => {
        setHasUnsavedChanges(false);
        try {
            if (handleSaveInParent) {
                handleSaveInParent();
            } else {
                await fetchWithAuth(
                    `${API_URL}/climatisation/info`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(editedClimatisationInfo),
                    },
                    session,
                );
                toast.success("Informatie m.b.t. klimatisatie van unit succesvol bijgewerkt!");
            }
        } catch (e) {
            console.error(e);
            toast.error("Er is een fout opgetreden bij het bijwerken van de klimatisatieinformatie.");
        }
    };

    const handleCloseAddDeviceModal = () => {
        setIsAddDeviceModalOpen(false);
    };

    const handleCloseDeviceDetailModal = () => {
        selectedDevice && setSelectedDevice(null);
        setIsDeviceDetailModalOpen(false);
    };

    const updateClimatisationInfo = async (updatedFields: any) => {
        setHasUnsavedChanges(true);
        setEditedClimatisationInfo((prev: any) => ({ ...prev, ...updatedFields }));

        if (handleClimatisationInfoChangeInParent) {
            handleClimatisationInfoChangeInParent({
                ...climatisationInfo,
                ...updatedFields,
            });
        } else {
            try {
                // Create minimal patch object
                const patchObject = {
                    id: climatisationInfo?.id,
                    ...updatedFields,
                    buildingId: building?.id,
                    unitId: unit?.id
                };

                await fetchWithAuth(
                    `${API_URL}/climatisation/info`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(patchObject),
                    },
                    session
                );

                toast.success("Klimatisatie informatie succesvol bijgewerkt");
            } catch (error) {
                console.error(error);
                toast.error("Er is een fout opgetreden bij het bijwerken van de klimatisatie informatie");
            }
        }
    };

    const handleOpenAddDeviceModal = () => {
        setIsAddDeviceModalOpen(true);
    };

    const getStatusColor = (status: any) => {
        switch (status) {
            case UnitConformityStatus.OK:
            case UnitConformityStatus.NOT_REQUIRED:
                return "bg-green-500"; // Green for OK
            case UnitConformityStatus.PARTIAL_OK:
                return "bg-orange-500"; // Orange for Partial
            case UnitConformityStatus.NOT_OK:
                return "bg-red-500"; // Red for Not OK
            default:
                return "bg-transparent-500";
        }
    };

    const handleOpenViewDeviceModal = (device: any) => {
        if (!device) return toast.error("Er is een fout opgetreden bij het openen van het toestel.");
        setSelectedDevice(device);
        setIsDeviceDetailModalOpen(true);
    };


    return (
        <section className="flex flex-col gap-0 rounded-md bg-white">
            <SectionHeader
                title="Informatie"
                icon={<Document />}
                editMode={editMode}
                sticky={false}
                onEditClick={() => setEditMode(!editMode)}
                onSaveClick={handleSave}
                showEditToggle={true}
                hasUnsavedChanges={hasUnsavedChanges}
                onCloseClick={() => { if (handleCloseParent) handleCloseParent() }}
            />
            <div className="p-3">
                <div className="gap-3 sm:grid sm:grid-cols-2">
                    {/* Conformity score */}
                    <div className={`flex flex-col gap-3 rounded-md border p-3`}>
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30`} />
                            <p>Opmerkingen bij de klimatisatie van {building ? "het gebouw" : "de unit"}</p>
                        </header>
                        <EditableInputComponent
                            label="Opmerkingen"
                            value={editedClimatisationInfo?.remarks as any || ""}
                            editMode={editMode}
                            isLoading={false}
                            onChange={(value) => updateClimatisationInfo({ remarks: value })}
                        />
                    </div>

                    {/* Connected to */}
                    {unit && <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                                editedClimatisationInfo && editedClimatisationInfo?.connectedTo,
                            )}`} />
                            <p>Heeft de unit een eigen klimatisatiesysteem voor verwarming en/of koeling of is het verbonden aan het centraal systeem voor het gebouw?</p>
                        </header>
                        <EditableSelectComponent
                            label="Maak een keuze"
                            value={editedClimatisationInfo && editedClimatisationInfo?.connectedTo || ""}
                            isLoading={false}
                            options={Object.values(HeatingConnectedTo).map(value => ({ label: value, value }))}
                            editMode={editMode}
                            onChange={(value) => updateClimatisationInfo({ connectedTo: value })}
                        />
                    </div>}

                    {/* Source */}
                    {unit && <div className="flex flex-col gap-3 rounded-md border border-neutral-primary/20 p-3">
                        <header className="flex items-center gap-2 text-sm font-medium">
                            <span className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                                editedClimatisationInfo && editedClimatisationInfo?.source,
                            )}`} />
                            <p>Wat is de energiebron voor de klimatisatie?</p>
                        </header>
                        <EditableSelectComponent
                            label="Maak een keuze"
                            value={editedClimatisationInfo && editedClimatisationInfo?.source || ""}
                            isLoading={false}
                            options={Object.values(ClimatisationSource).map(value => ({ label: value, value }))}
                            editMode={editMode}
                            onChange={(value) => updateClimatisationInfo({ source: value })}
                        />
                    </div>}
                </div>
                <section className="flex flex-col gap-0 rounded-md bg-white mt-6">
                    <div key="devices" className="col-span-full flex flex-col gap-3">
                        <header className="flex justify-between">
                            <div className="flex items-center gap-2">
                                <Appliances className="h-[18px] w-[18px]" />{" "}
                                <p className="text-lg">Toestellen</p>
                            </div>
                            <ButtonNew
                                onClick={() => handleOpenAddDeviceModal()}
                                icon={<Plus />}
                                label="Toestel toevoegen"
                                variant="primaryMoss"
                            />
                        </header>
                        <Table
                            classNames={{
                                wrapper: "shadow-none rounded-none p-0",
                                thead: "h-fit border-b-2 border-neutral-primary/40",
                                th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
                                tbody: "border-b divide-y divide-neutral-primary/15",
                                tr: "divide-x divide-neutral-primary/15",
                                td: "py-2.5",
                            }}
                        >
                            <TableHeader>
                                <TableColumn>Type</TableColumn>
                                <TableColumn>Subtype</TableColumn>
                                <TableColumn>Merk</TableColumn>
                                <TableColumn>Model</TableColumn>
                                <TableColumn>Drijft aan</TableColumn>
                                <TableColumn>Welke kamers</TableColumn>
                                <TableColumn>Locatie</TableColumn>
                                {/* <TableColumn>Technische Fiche</TableColumn> */}
                                <TableColumn>Detail</TableColumn>
                            </TableHeader>
                            <TableBody>
                                {climatisationDevices && climatisationDevices.map((device: ClimatisationDevice, index: any) => (
                                    <TableRow key={index}>

                                        <TableCell>{device.deviceType}</TableCell>
                                        <TableCell>{device.deviceSubType}</TableCell>
                                        <TableCell>{device.brand}</TableCell>
                                        <TableCell>{device.model}</TableCell>
                                        <TableCell>{device.drives}</TableCell>
                                        <TableCell>{device.drivesWhichRooms || "N/A"}</TableCell>
                                        <TableCell>{device.deviceLocation || "N/A"}</TableCell>
                                        {/* <TableCell>
                                                    {device.maintenanceAttestFileLogs ? (
                                                        <a
                                                            href={device.invoiceFileLog.sharePointFileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <span className="flex gap-2">
                                                                Bekijk document{" "}
                                                                <Document className="cursor-pointer" />
                                                            </span>
                                                        </a>
                                                    ) : (
                                                        "N/A"
                                                    )}
                                                </TableCell> */}
                                        <TableCell>
                                            <span
                                                onClick={() => handleOpenViewDeviceModal(device)}
                                                className="cursor-pointer bg-transparent text-moss-primary"
                                            >
                                                <Eye />
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    {selectedDevice && (
                        <ClimatisationDeviceDetailModal
                            isOpen={isDeviceDetailModalOpen}
                            onClose={handleCloseDeviceDetailModal}
                            unit={unit ? unit : null}
                            building={building ? building : null}
                            climatisationDevice={selectedDevice ? selectedDevice : null}
                            fetchDevices={fetchDevices}
                        />
                    )}
                    {
                        <ClimatisationDeviceDetailModal
                            isOpen={isAddDeviceModalOpen}
                            onClose={handleCloseAddDeviceModal}
                            unit={unit ? unit : null}
                            building={building ? building : null}
                            climatisationInfo={climatisationInfo}
                            climatisationDevice={{ climatisationInfo: { id: climatisationInfo.id } }}
                            fetchDevices={fetchDevices}
                        />
                    }
                </section>
                {/*
                  Meters worden nu beheerd als voorzieningen (assets), niet meer hier.
                  Sectie verborgen — zie Instellingen > "Meters in documenten".
                  {climatisationInfo && climatisationInfo.id && (
                      <GasMeters
                          meters={climatisationInfo.gasMeters}
                          climatisationInfoId={climatisationInfo && climatisationInfo.id}
                      />
                  )}
                */}
            </div>
        </section>
    );
};

export default ClimatisationInfoComponent;
